import { useState, useEffect, useRef } from 'react';
import "../App.css";
import UserStats from './UserStats';
import LevelHUD from "./LevelHUD";
import ClassroomScene from "./ClassroomScene";
import Burger from './Burger';
import { calculateStats } from '../hooks/calculateStats';
import { useNavigate } from 'react-router-dom';
import { getSkyGradientForHour } from '../utils/dayNightColors';

// =============================
// CLASSE DEL MODELLO 1R1C
// =============================
class OneR1CModel {
  constructor(R, C, initialTemp, dt) {
    this.R = R;
    this.C = C;
    this.Ti = initialTemp;
    this.dt = dt; // dt fisso: 3600 sec = 1 ora
  }
  // Calcola il nuovo valore di temperatura interna
  step(Tout, Q, isWindowOpen) {
    const additionalLoss = isWindowOpen ? 1.5 : 1.0;
    const dT = (this.dt / this.C) * ((Tout - this.Ti) / (this.R / additionalLoss) + Q);
    this.Ti += dT;
    return this.Ti;
  }
}

// =============================
// FUNZIONE: restituisce il background gradient in base all'ora di gioco
// =============================
const GradientBackground = ({ gameHour, children }) => {
  const newGradient = getSkyGradientForHour(gameHour);
  const [currentGradient, setCurrentGradient] = useState(newGradient);
  const [overlayGradient, setOverlayGradient] = useState(null);
  const [overlayOpacity, setOverlayOpacity] = useState(0);

  useEffect(() => {
    // Se il gradient calcolato è diverso da quello corrente, inizia la transizione
    if (newGradient !== currentGradient) {
      setOverlayGradient(newGradient);
      setOverlayOpacity(0); // assicura opacità iniziale a 0
      // Dopo un breve ritardo, avvia la transizione portando l'opacità a 1
      const timeout = setTimeout(() => {
        setOverlayOpacity(1);
      }, 50);
      return () => clearTimeout(timeout);
    }
  }, [newGradient, currentGradient]);

  // Quando la transizione è completata, aggiorna il background corrente
  const handleTransitionEnd = () => {
    if (overlayOpacity === 1) {
      setCurrentGradient(overlayGradient);
      setOverlayGradient(null);
      setOverlayOpacity(0);
    }
  };

  return (
    <div style={{ position: 'absolute', width: '100vw', height: '100vh', overflow: 'hidden' }}>
      {/* Layer di base con il gradient corrente */}
      <div
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          background: currentGradient,
        }}
      />
      {/* Layer in sovrapposizione per il crossfade */}
      {overlayGradient && (
        <div
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            background: overlayGradient,
            opacity: overlayOpacity,
            transition: 'opacity 1s ease-in-out',
          }}
          onTransitionEnd={handleTransitionEnd}
        />
      )}
      {children}
    </div>
  );
};

// =============================
// FUNZIONE: restituisce il prezzo per fascia oraria (in DKK/kWh)
// =============================
const getHourlyPrice = (hourOfDay) => {
  if (hourOfDay >= 0 && hourOfDay < 4) {
    return 0.5;
  } else if (hourOfDay >= 4 && hourOfDay < 8) {
    return 0.9;
  } else if (hourOfDay >= 8 && hourOfDay < 12) {
    return 0.7;
  } else if (hourOfDay >= 12 && hourOfDay < 16) {
    return 0.7;
  } else if (hourOfDay >= 16 && hourOfDay < 20) {
    return 0.9;
  } else {
    return 0.5; // 20-24
  }
};

// =============================
// COMPONENTE PRINCIPALE LEVEL2
// =============================
const Level2 = ({ onFinish }) => {
  const navigate = useNavigate();
  const [totalPenaltyCost, setTotalPenaltyCost] = useState(0);
  const [ambientIntensity, setAmbientIntensity] = useState(0);

  useEffect(() => {
    let step = 0.02;
    let interval = setInterval(() => {
      setTimeout(() => {
        setAmbientIntensity(prev => {
          if (prev >= 0.9) {
            clearInterval(interval);
            return 0.9;
          }
          return prev + step;
        });
      }, 300);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // PARAMETRI DI SIMULAZIONE
  const R = 0.0001344;
  const C = 6.35e9;
  const initialTemp = 20;
  const dt = 3600; // 1 ora
  const simSpeed = 0.25; // velocità di simulazione
  const totalHours = 3 * 24; // 5 giorni simulati
  const maxHeatingPower = 501000; // W
  const halfPowerThreshold = maxHeatingPower / 2; // 250000 W
  const radiatorMaxPower = maxHeatingPower / 3;
  const radiatorPenaltyThreshold = radiatorMaxPower * 0.5;
  const penaltyMultiplier = 1.5;
  const integralErrorRef = useRef(0);
  const optimalEmissions = 9000000;
  const optimalCost = 12500;

  // STATE DI SIMULAZIONE E UI
  const [showPopup, setShowPopup] = useState(true);
  const [pointers, setPointers] = useState([{ value: 100000 }]);
  const heatingPower = pointers[0].value;
  const [temperatureData, setTemperatureData] = useState([]);
  const initialZone = localStorage.getItem('zone') || 'DK1';
  const [selectedZone, setSelectedZone] = useState(initialZone);
  const [simulationRunning, setSimulationRunning] = useState(false);
  const [autoMode, setAutoMode] = useState(false);
  const [currentHour, setCurrentHour] = useState(0);
  const [currentIndoorTemp, setCurrentIndoorTemp] = useState(initialTemp);
  const [currentOutdoorTemp, setCurrentOutdoorTemp] = useState(-5);
  const [results, setResults] = useState([]);
  const [totalCost, setTotalCost] = useState(0);
  const [currentHourlyCost, setCurrentHourlyCost] = useState(0);
  const [totalEnergyConsumed, setTotalEnergyConsumed] = useState(0);
  const [totalEmissions, setTotalEmissions] = useState(0);
  const [points, setPoints] = useState(0);
  const [radiatorPipeValues, setRadiatorPipeValues] = useState([0, 0, 0]);

  useEffect(() => {
    setAutoMode(false);
  }, []);

  const sliderRef = useRef(null);

  // Funzione per gestire il pulsante Play/Pause
  const handlePlay = () => {
    if (sliderRef.current) {
      sliderRef.current.blur();
    }
    toggleSimulation();
  };

  // Riferimenti per il modello e la temperatura interna
  const modelRef = useRef(new OneR1CModel(R, C, initialTemp, dt));
  const indoorTempRef = useRef(initialTemp);

  // ----------------------------
  // CARICAMENTO DEL FILE DELLE TEMPERATURE
  // ----------------------------
  useEffect(() => {
    const temperatureFile =
      selectedZone === 'DK1'
        ? 'DH_AARHUS_TEMPERATURE_DK1_EMISSIONS_CO2LEVEL.csv'
        : 'DH_COPENHAGEN_TEMPERATURE_DK2_EMISSIONS_CO2LEVEL.csv';

    const temperatureHeader = selectedZone === 'DK1'
      ? 'Temperature_Aarhus'
      : 'Temperature_Copenhagen';

    fetch(temperatureFile)
      .then((res) => res.text())
      .then((text) => {
        const lines = text.split('\n').filter((line) => line.trim() !== '');
        const headers = lines[0].split(';').map((h) => h.trim());
        const tempIndex = headers.indexOf(temperatureHeader);
        const emissionIndex = headers.indexOf('EmissionFactor');

        const temps = lines.slice(1, 1 + 7 * 24).map((line) => {
          const values = line.split(';');
          return {
            temperature: parseFloat(values[tempIndex]),
            emissionFactor: parseFloat(values[emissionIndex]),
          };
        });

        setTemperatureData(temps);
      })
      .catch((err) =>
        console.error('Errore nel caricamento del file delle temperature:', err)
      );
  }, [selectedZone]);

  // ----------------------------
  // FUNZIONE simulationStep: esegue 1 ora di simulazione
  // ----------------------------
  const simulationStep = () => {
    const hourOfDay = currentHour % 24;

    // Ottieni la temperatura esterna
    const Tout =
      temperatureData.length > currentHour
        ? temperatureData[currentHour].temperature
        : -5 + 10 * Math.sin((2 * Math.PI * hourOfDay) / 24);

    // Utilizza la nostra tabella di prezzo fissa
    const hourlyPrice = getHourlyPrice(hourOfDay);

    const isWindowOpen = hourOfDay >= 12 && hourOfDay < 14;
    let Q = heatingPower;

    if (autoMode) {
      const additionalLoss = isWindowOpen ? 1.5 : 1.0;
      const isOccupied = (hourOfDay >= 8 && hourOfDay < 17);

      // Imposta il target in base alla presenza
      let targetTemp = isOccupied ? 21 : 19;
      if (!isOccupied) {
        const nextHour = (hourOfDay + 1) % 24;
        if (nextHour >= 8 && nextHour < 17) {
          targetTemp = 21;
        }
      }

      const error = targetTemp - indoorTempRef.current;
      if (error * integralErrorRef.current < 0) {
        integralErrorRef.current = 0;
      }
      integralErrorRef.current += error;

      // Guadagni PID differenti in base all’occupazione
      let kP, kI;
      if (isOccupied) {
        kP = 35000;
        kI = 3000;
      } else {
        kP = 10000;
        kI = 500;
      }

      Q = additionalLoss * (kP * error + kI * integralErrorRef.current);
      if (Q < 0) Q = 0;
      if (isOccupied && indoorTempRef.current < 20) {
        Q = maxHeatingPower;
      }
      if (Q > halfPowerThreshold) {
        const excess = Q - halfPowerThreshold;
        const maxExcess = maxHeatingPower - halfPowerThreshold;
        const scaledExcess = maxExcess * Math.tanh(excess / maxExcess);
        Q = halfPowerThreshold + scaledExcess;
      }
      if (
        isOccupied &&
        hourlyPrice === 0.9 &&
        indoorTempRef.current > targetTemp
      ) {
        Q = Q * 0.9;
      }
      Q = Math.min(Q, maxHeatingPower);

      setPointers([{ value: Math.round(Q) }]);
      console.log(
        `Hour: ${currentHour} | Target: ${targetTemp.toFixed(2)}°C | Indoor: ${indoorTempRef.current.toFixed(2)}°C | Error: ${error.toFixed(2)} | Integral: ${integralErrorRef.current.toFixed(2)} | Q: ${Q.toFixed(2)} W`
      );
    }

    // Calcola la nuova temperatura interna
    const newIndoorTemp = modelRef.current.step(Tout, Q, isWindowOpen);
    indoorTempRef.current = newIndoorTemp;
    setCurrentIndoorTemp(newIndoorTemp);
    setCurrentOutdoorTemp(Tout);

    // Energia consumata in kWh (Q in Watt)
    const energyConsumed = Math.abs(Q) / 1000;
    setTotalEnergyConsumed(prev => prev + energyConsumed);

    const currentTempData = temperatureData[currentHour];
    const currentEmissionFactor = currentTempData ? currentTempData.emissionFactor : 200;
    const hourlyEmissions = energyConsumed * currentEmissionFactor;
    setTotalEmissions(prev => prev + hourlyEmissions);

    /*
     New rules
     * * If the difference between the throttles is over 25% the indoor climate score gets halved.
       * Assign the fee by 1/3 to each radiator when above 50%
       * If the radiator is over 50% we multiply by 0.9 the hourly indoor climate score
     */

    let penaltyCost = 0;
    radiatorPipeValues.forEach((radiatorPower) => {
      if (radiatorPower > radiatorPenaltyThreshold) {
        const excessPower = radiatorPower - radiatorPenaltyThreshold;
        const excessEnergy = excessPower / 1000;
        penaltyCost += excessEnergy * hourlyPrice * (penaltyMultiplier - 1);
      }
    });

    const stepCost = energyConsumed * hourlyPrice + penaltyCost;
    setTotalCost(prev => prev + stepCost);
    setCurrentHourlyCost(stepCost);
    setTotalPenaltyCost(prev => prev + penaltyCost);

    const throttleSpreadPct = (Math.max(...radiatorPipeValues) - Math.min(...radiatorPipeValues)) / radiatorMaxPower;
    const radiatorsOverHalfCount = radiatorPipeValues.filter(
      (radiatorPower) => radiatorPower > radiatorPenaltyThreshold,
    ).length;

    let pointsThisStep = 0;
    if (hourOfDay >= 8 && hourOfDay < 17 && newIndoorTemp >= 20 && newIndoorTemp <= 23) {
      pointsThisStep = throttleSpreadPct > 0.25 ? 0.5 : 1;
      pointsThisStep *= Math.pow(0.9, radiatorsOverHalfCount);
    }
    setPoints(prev => prev + pointsThisStep);

    setResults(prev => [
      ...prev,
      {
        time: currentHour / 24,
        indoor: newIndoorTemp,
        outdoor: Tout,
        heatingPower: Q,
        isWindowOpen,
        stepCost: stepCost.toFixed(2),
      }
    ]);

    setCurrentHour(prev => prev + 1);
  };
  
  // ----------------------------
  // LOOP DI SIMULAZIONE
  // ----------------------------
  useEffect(() => {
    let timer;
    if (simulationRunning && currentHour < totalHours) {
      timer = setTimeout(() => {
        simulationStep();
      }, 100 / simSpeed);
    } else if (currentHour >= totalHours) {
      if (autoMode) {
        setTotalCost(optimalCost);
        setTotalEmissions(optimalEmissions);
      }
      setSimulationRunning(false);
    }
    return () => clearTimeout(timer);
  }, [
    simulationRunning,
    totalHours,
    currentHour,
    simSpeed,
    autoMode,
    temperatureData,
    selectedZone,
    currentHourlyCost,
    totalPenaltyCost,
  ]);

  // ----------------------------
  // TOGGLE PLAY/PAUSE
  // ----------------------------
  const toggleSimulation = () => {
    if (currentHour >= totalHours) return;
    setSimulationRunning(prev => !prev);
  };

  // ----------------------------
  // FORMATTAZIONE ORARIA PER LA UI
  // ----------------------------
  const formatTimeDisplay = (hour) => {
    const daysPassed = Math.floor(hour / 24);
    const hourOfDay = hour % 24;
    return `Day ${daysPassed}, ${hourOfDay.toString().padStart(2, '0')}:00`;
  };

  // ----------------------------
  // ELEMENTI PER IL GRAFICO
  // ----------------------------
  const viewWindow = 2;
  const currentTimeInDays = currentHour / 24;
  const xDomain = [currentTimeInDays - viewWindow / 2, currentTimeInDays + viewWindow / 2 + 0.2];
  const hourOfDay = currentHour % 24;
  const isDaytime = hourOfDay >= 8 && hourOfDay < 16;

  const tickStepHours = 8;
  const windowStartHours = xDomain[0] * 24;
  const windowEndHours = xDomain[1] * 24;
  const firstTickHours = Math.ceil(windowStartHours / tickStepHours) * tickStepHours;
  const ticks = [];

  for (let h = firstTickHours; h <= windowEndHours; h += tickStepHours) {
    ticks.push(h / 24);
  }

  // Schermata di fine simulazione
  if (currentHour >= totalHours) {
    const statsFromCalc = calculateStats(results, totalCost, totalEmissions, totalEnergyConsumed, points);
    const { totalScore = 0 } = statsFromCalc;
    const statsToReturn = {
      totalPenaltyCost,
      totalEnergyConsumed,
      totalCost,
      totalEmissions,
      totalScore,
      points,
      ...statsFromCalc
    };

    return (
      <div
        className="game-over-screen slide-in-left-transition"
        style={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#eef6ff',
          background: '#F90293'
        }}
      >
        <h2>Perfect!</h2>
        <p>Level 1 completed.</p>
        {/* <p><strong>Energy Consumed:</strong> {(totalEnergyConsumed / 1000).toFixed(2)} MWh</p>
        <p><strong>Total Emissions:</strong> {(totalEmissions / 1000).toFixed(2)} kg CO₂</p>
        <p><strong>Total Cost:</strong> {totalCost.toFixed(2)} DKK</p>
        <p><strong>Total Penalty:</strong> {totalPenaltyCost.toFixed(2)} DKK</p>
        <p><strong>Cost Score:</strong>{Number(statsFromCalc.costScore).toFixed(2)}</p>
      <p><strong>Emission Score:</strong> {Number(statsFromCalc.emissionScore).toFixed(2)}</p>
      <p><strong>Comfort Score:</strong> {Number(statsFromCalc.comfortScore).toFixed(2)}</p>
      <p><strong>Total Score:</strong> {Number(statsFromCalc.finalScore).toFixed(2)}</p> */}
        <button className='next_level_button' onClick={() => {
            onFinish(statsToReturn);
          }} style={{ marginTop: '20px' }}>
          Next Level
        </button>
      </div>
    );
  }

  const nightBands = [];
  for (let day = -2; day < 7; day++) {
    const nightStart = day + 16 / 24;
    const nightEnd = day + 1 + 8 / 24;
    const clampedStart = Math.max(nightStart, xDomain[0]);
    const clampedEnd = Math.min(nightEnd, xDomain[1]);
    
    if (clampedEnd > clampedStart) {
      nightBands.push({
        key: `night-${day}`,
        x1: clampedStart,
        x2: clampedEnd,
        y1: 17,
        y2: 26,
      });
    }
  }

  const comfortBand = {
    key: 'comfort-band',
    x1: xDomain[0],
    x2: xDomain[1],
    y1: 20,
    y2: 23,
  };

  const CustomBands = ({ xAxisMap, yAxisMap }) => {
    const xAxis = xAxisMap[Object.keys(xAxisMap)[0]];
    const yAxis = yAxisMap[Object.keys(yAxisMap)[0]];
    
    if (!xAxis || !yAxis) return null;

    const xScale = xAxis.scale;
    const yScale = yAxis.scale;

    const toRect = (band, fill, opacity) => {
      const x1 = xScale(band.x1);
      const x2 = xScale(band.x2);
      const width = x2 - x1;
      if (width <= 0) return null;
      return (
        <rect
          key={band.key}
          x={x1}
          y={yScale(band.y2)}
          width={width}
          height={yScale(band.y1) - yScale(band.y2)}
          rx={10}
          ry={10}
          fill={fill}
          fillOpacity={opacity}
        />
      );
    };

    return (
      <g>
        {nightBands.map((band) => toRect(band, "#4cb944", 1))}
        {toRect(comfortBand, "#4cb944", 1)}
      </g>
    );
  };

  // ----------------------------
  // RENDER DELLA UI
  // ----------------------------
  return (
    <div
      className="simulation-ui"
      style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}
    >
      <Burger />
      {showPopup && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 99999999,
          }}
        >
          <div
            className="level-wrapper"
            style={{
              padding: "20px",
              boxShadow: "0 0px 25px rgba(0,0,0,0.3)",
            }}
          >
            <img
              src="/level1.png"
              alt="Price level"
              style={{ width: "130px" }}
            />
            <p>
              🔥 <strong>Så er det tid til at teste dine evner!</strong> ❄️
            </p>
            <p>
              Justér termostaterne på skolen og hold eleverne
              <strong>komfortable</strong> 😌.
            </p>
            <p>
              🌡️ <strong>For koldt?</strong> 🥶 Så fryser de!
            </p>
            <p>
              🌞 <strong>For varmt?</strong> 😓 Så sveder de!
            </p>
            <p>
              Kan du finde den <strong>perfekte balance</strong> ⚖️ og samtidig
              <strong>spare energi</strong> ⚡,
              <strong>reducere CO₂-udledningen</strong> 🌍 og
              <strong>sænke omkostningerne</strong> 💰?
            </p>
            <p>
              ⚠️ <strong>Men pas på</strong> — skruer du for meget op for varmen
              🔥, kan det føre til{" "}
              <strong>bøder for høj returtemperatur</strong> 🚨 fra din
              fjernvarmeleverandør!
            </p>
            <button
              className="play-button"
              onClick={() => setShowPopup(false)}
              style={{ marginTop: "10px" }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      <header
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 10000,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "10px",
          boxSizing: "border-box",
        }}
      >
        <div className="time-container">
          <button
            type="button"
            onClick={() => navigate("/")}
            aria-label="Go to homepage"
            style={{
              position: "absolute",
              top: "24px",
              left: "24px",
              background: "none",
              border: "none",
              padding: 0,
            }}
          >
            <img src="/neonlogo.png" alt="" style={{ width: "120px" }} />
          </button>
          <div className="time-and-date">{formatTimeDisplay(currentHour)}</div>
        </div>
      </header>

      <main style={{ flex: 1, display: "flex" }}>
        <aside
          className="slide-in-left-transition"
          style={{
            padding: "20px",
            zIndex: "1000",
            color: "#F8F9FA",
          }}
        >
          <div className="point-container">
            <UserStats
              results={results}
              totalCost={totalCost}
              totalEmissions={totalEmissions}
              totalEnergyConsumed={totalEnergyConsumed}
              points={points}
            />
            <select
              id="zoneSelector"
              value={selectedZone}
              onChange={(e) => {
                setSelectedZone(e.target.value);
                setCurrentHour(0);
                setTemperatureData([]);
              }}
              style={{ display: "none", marginTop: "5px" }}
            >
              <option value="DK1">DK1</option>
              <option value="DK2">DK2</option>
            </select>
          </div>
        </aside>
        <ClassroomScene
          GradientBackground={GradientBackground}
          currentHour={currentHour}
          ambientIntensity={ambientIntensity}
          isDaytime={isDaytime}
          currentIndoorTemp={currentIndoorTemp}
          radiatorPipeValues={radiatorPipeValues}
          maxHeatingPower={maxHeatingPower}
        />
        <LevelHUD
          totalEmissions={totalEmissions}
          currentOutdoorTemp={currentOutdoorTemp}
          totalCost={totalCost}
          totalEnergyConsumed={totalEnergyConsumed}
          currentIndoorTemp={currentIndoorTemp}
          results={results}
          CustomBands={CustomBands}
          xDomain={xDomain}
          ticks={ticks}
          electricityPrices={null}
          temperatureData={null}
          currentHour={currentHour}
          handlePlay={handlePlay}
          simulationRunning={simulationRunning}
          sliderRef={sliderRef}
          maxHeatingPower={maxHeatingPower}
          heatingPower={heatingPower}
          onHeatingPowerChange={(value) => setPointers([{ value }])}
          onSmallPipesChange={(nextValues) => setRadiatorPipeValues(nextValues)}
        />
      </main>
    </div>
  );
};

export default Level2;
